import React, { useState, useMemo } from 'react';
import { Info } from 'lucide-react';
import { formatDate } from '../utils/dateFormatting';
import { Table, Badge } from './ui';

const AllClientsTable = ({
  clients,
  onRowClick,
  onClientUpdate,
  visibleColumns: externalVisibleColumns,
  autoResizeEnabled = true,
}) => {
  // Use external visibility state if provided, otherwise use local state
  const [localVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('all-clients-visible-columns');
    return saved ? JSON.parse(saved) : {
      action: true,
      name: true,
      billingStatus: true,
      yearlyCost: true,
      clientsInvoicedDate: true,
      clientOf: true,
      clientStatus: true,
      state: true,
      platform: true,
      address: false,
      phoneNumber: false,
    };
  });

  const visibleColumns = externalVisibleColumns || localVisibleColumns;
  const [localAutoResizeEnabled, setLocalAutoResizeEnabled] = useState(true);
  const currentAutoResizeEnabled = externalVisibleColumns ? autoResizeEnabled : localAutoResizeEnabled;

  // Handle manual column resize - auto-disable auto-resize
  const handleManualResize = () => {
    if (currentAutoResizeEnabled && !externalVisibleColumns) {
      setLocalAutoResizeEnabled(false);
    }
  };

  // Format price
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '—';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  // Get status badge variant
  const getStatusVariant = (status, type) => {
    if (!status) return 'neutral';

    // Check for specific unset/neutral values
    const upperStatus = status.toUpperCase();
    if (upperStatus === "NO SUPPORT NEEDED" ||
        upperStatus === "UNKNOWN" ||
        upperStatus === "UNLIKELY CUSTOMER" ||
        upperStatus === "NOT SET" ||
        upperStatus === "NAME CHANGE") {
      return 'neutral';
    }

    if (type === 'billing') {
      if (status === "Ready to Invoice") return 'warning';
      if (status === "Invoiced") return 'info';
      if (status === "Paid") return 'success';
      if (status === "Overdue") return 'danger';
      if (status === "Quoted") return 'warning';
      if (status === "Voided") return 'danger';
    }

    if (type === 'client') {
      if (status === "Active" || status === "Active Client" || status === "ACTIVE CLIENT") return 'success';
      if (status === "Inactive") return 'neutral';
      if (status === "Pending") return 'warning';
      if (status === "Part Time Client" || status === "PART TIME CLIENT") return 'info';
      if (status === "Dropped" || status === "Lost Client") return 'danger';
    }

    return 'neutral';
  };

  // Define all columns for the Table component
  const allColumns = [
    {
      id: 'action',
      label: '',
      field: 'id',
      width: 44,
      minWidth: 44,
      maxWidth: 44,
      sortable: false,
      searchable: false,
      resizable: false,
      align: 'center',
      contentType: 'icon',
      render: (value, row) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRowClick(row);
          }}
          style={{
            padding: '4px',
            background: 'none',
            border: 'none',
            color: 'var(--gray-400)',
            cursor: 'pointer',
            borderRadius: '6px',
            transition: 'background-color 0.2s, color 0.2s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--surface-hover)';
            e.currentTarget.style.color = 'var(--cta-blue)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--gray-400)';
          }}
          title="View Details"
        >
          <Info size={16} />
        </button>
      ),
    },
    {
      id: 'name',
      label: 'Client Name',
      field: 'name',
      width: 200,
      minWidth: 130,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <span style={{ fontWeight: 'var(--font-medium)', color: 'var(--gray-200)' }}>
          {value || '—'}
        </span>
      ),
    },
    {
      id: 'billingStatus',
      label: 'Billing',
      field: 'billing_status',
      width: 120,
      minWidth: 90,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'billing')} size="sm">
          {value}
        </Badge>
      ) : (
        <Badge variant="neutral" size="sm">Not Set</Badge>
      ),
    },
    {
      id: 'yearlyCost',
      label: 'Yearly Cost',
      field: 'yearly_cost',
      width: 110,
      minWidth: 90,
      sortable: true,
      resizable: true,
      contentType: 'number',
      align: 'right',
      render: (value) => (
        <span style={{
          color: value ? 'var(--success)' : 'var(--gray-500)',
          fontWeight: value ? 'var(--font-medium)' : 'normal'
        }}>
          {formatCurrency(value)}
        </span>
      ),
    },
    {
      id: 'clientsInvoicedDate',
      label: 'Invoiced',
      field: 'clients_invoiced_date',
      width: 105,
      minWidth: 95,
      sortable: true,
      resizable: true,
      contentType: 'date',
      render: (value) => value ? formatDate(value) : '—',
    },
    {
      id: 'clientOf',
      label: 'Client Of',
      field: 'client_of',
      width: 130,
      minWidth: 90,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'clientStatus',
      label: 'Status',
      field: 'client_status',
      width: 130,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'badge',
      render: (value) => value ? (
        <Badge variant={getStatusVariant(value, 'client')} size="sm">
          {value}
        </Badge>
      ) : (
        <Badge variant="neutral" size="sm">Not Set</Badge>
      ),
    },
    {
      id: 'state',
      label: 'State',
      field: 'state',
      width: 100,
      minWidth: 80,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'platform',
      label: 'Platform',
      field: 'platform',
      width: 140,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
    {
      id: 'address',
      label: 'Address',
      field: 'address',
      width: 200,
      minWidth: 130,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => (
        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || '—'}
        </div>
      ),
    },
    {
      id: 'phoneNumber',
      label: 'Phone',
      field: 'company_phone',
      width: 130,
      minWidth: 100,
      sortable: true,
      searchable: true,
      resizable: true,
      contentType: 'text',
      render: (value) => value || '—',
    },
  ];

  // Get visible columns for table display
  const columns = useMemo(() => {
    return allColumns.filter(col => visibleColumns[col.id]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns]);

  return (
    <Table
      data={clients}
      columns={columns}
      sortable={true}
      searchable={false}
      resizable={true}
      paginated={false}
      storageKey="all-clients-table"
      autoResizeOnDataChange={currentAutoResizeEnabled}
      showAutoSizeButton={false}
      disableResizeListener={true}
      allowNaturalWidth={true}
      onManualResize={handleManualResize}
      emptyMessage="No clients found"
      onRowClick={onRowClick}
      variant="dense"
      stickyHeader={true}
      style={{ border: 'none', borderRadius: '0', width: '100%', overflow: 'visible' }}
    />
  );
};

export default AllClientsTable;
